/**
 * API Service for communicating with Supabase backend
 * Replaces Google Apps Script backend
 */

import { supabase } from './supabase.js'

// ============================================
// Workouts API
// ============================================

/**
 * Fetch all workouts from server
 */
export async function fetchWorkouts() {
  const { data, error } = await supabase
    .from('workouts')
    .select(`
      *,
      exercises (*)
    `)
    .order('date', { ascending: false })

  if (error) {
    console.error('Error fetching workouts:', error)
    throw new Error(error.message)
  }

  return data
}

/**
 * Fetch a single workout by ID
 */
export async function fetchWorkout(id) {
  const { data, error } = await supabase
    .from('workouts')
    .select(`
      *,
      exercises (*)
    `)
    .eq('id', id)
    .single()

  if (error) {
    console.error('Error fetching workout:', error)
    throw new Error(error.message)
  }

  return data
}

/**
 * Save workout to server (create or update)
 */
export async function saveWorkoutToServer(workout) {
  try {
    const { exercises, ...workoutData } = workout

    // Get current user ID
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    // Upsert workout
    const { data: savedWorkout, error: workoutError } = await supabase
      .from('workouts')
      .upsert({
        id: workoutData.id,
        user_id: user.id,  // Add user_id
        date: workoutData.date,
        type: workoutData.type,
        duration: workoutData.duration,
        notes: workoutData.notes
      })
      .select()
      .single()

    if (workoutError) throw workoutError

    // If there are exercises, handle them
    if (exercises && exercises.length > 0) {
      // Delete old exercises for this workout
      await supabase
        .from('exercises')
        .delete()
        .eq('workout_id', savedWorkout.id)

      // Insert new exercises
      const exercisesToInsert = exercises.map(ex => ({
        id: ex.id,
        workout_id: savedWorkout.id,
        user_id: user.id,  // Add user_id
        exercise_name: ex.exercise_name,
        sets: ex.sets, // PostgreSQL jsonb handles arrays natively
        reps: Array.isArray(ex.sets) && ex.sets.length > 0 ? ex.sets[0].reps : ex.reps,
        weight: Array.isArray(ex.sets) && ex.sets.length > 0 ? ex.sets[0].weight : ex.weight,
        rest_time: ex.rest_time
      }))

      const { error: exercisesError } = await supabase
        .from('exercises')
        .insert(exercisesToInsert)

      if (exercisesError) throw exercisesError
    }

    return savedWorkout
  } catch (error) {
    console.error('Error saving workout:', error)
    throw error
  }
}

/**
 * Delete workout from server
 */
export async function deleteWorkoutFromServer(id) {
  // Cascade delete will automatically remove exercises
  const { error } = await supabase
    .from('workouts')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting workout:', error)
    throw new Error(error.message)
  }

  return { success: true }
}

/**
 * Sync multiple workouts to server (legacy - for compatibility)
 */
export async function syncWorkouts(workouts) {
  const results = []

  for (const workout of workouts) {
    try {
      await saveWorkoutToServer(workout)
      results.push({ id: workout.id, success: true })
    } catch (error) {
      results.push({ id: workout.id, success: false, error: error.message })
    }
  }

  return results
}

/**
 * Batch sync multiple workouts to server (optimized)
 */
export async function batchSyncWorkouts(workouts) {
  try {
    // Get current user ID
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    // Separate workouts data and exercises
    const workoutsData = workouts.map(w => ({
      id: w.id,
      user_id: user.id,  // Add user_id
      date: w.date,
      type: w.type,
      duration: w.duration,
      notes: w.notes
    }))

    // Batch upsert workouts
    const { error: workoutsError } = await supabase
      .from('workouts')
      .upsert(workoutsData)

    if (workoutsError) throw workoutsError

    // Collect all exercises
    const allExercises = []
    workouts.forEach(workout => {
      if (workout.exercises && workout.exercises.length > 0) {
        workout.exercises.forEach(ex => {
          allExercises.push({
            id: ex.id,
            workout_id: workout.id,
            user_id: user.id,  // Add user_id
            exercise_name: ex.exercise_name,
            sets: ex.sets,
            reps: Array.isArray(ex.sets) && ex.sets.length > 0 ? ex.sets[0].reps : ex.reps,
            weight: Array.isArray(ex.sets) && ex.sets.length > 0 ? ex.sets[0].weight : ex.weight,
            rest_time: ex.rest_time
          })
        })
      }
    })

    if (allExercises.length > 0) {
      // Upsert exercises
      const { error: exercisesError } = await supabase
        .from('exercises')
        .upsert(allExercises)

      if (exercisesError) throw exercisesError
    }

    return workouts.map(w => ({ id: w.id, success: true }))
  } catch (error) {
    console.error('Error batch syncing workouts:', error)
    throw error
  }
}

/**
 * Clear all user workouts and replace with new ones (ULTRA-FAST for bulk import)
 * WARNING: This deletes ALL existing workouts and exercises!
 */
export async function clearAndReplaceWorkouts(workouts) {
  try {
    // Delete all user's workouts (cascade will handle exercises)
    const { error: deleteError } = await supabase
      .from('workouts')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000') // Delete all

    if (deleteError) throw deleteError

    // Use batch sync to insert all new workouts
    await batchSyncWorkouts(workouts)

    return {
      success: true,
      imported: workouts.length,
      message: `Imported ${workouts.length} workouts`
    }
  } catch (error) {
    console.error('Error in clearAndReplaceWorkouts:', error)
    throw error
  }
}

/**
 * Check if online and API is reachable
 */
export async function checkConnection() {
  if (!navigator.onLine) {
    return false
  }

  try {
    // Try a simple query to check connection
    const { error } = await supabase
      .from('workouts')
      .select('id')
      .limit(1)

    return !error
  } catch (error) {
    return false
  }
}

// ============================================
// Exercise Library API
// ============================================

/**
 * Fetch all custom exercises from server
 */
export async function fetchExerciseLibrary() {
  const { data, error } = await supabase
    .from('exercise_library')
    .select('*')
    .order('name')

  if (error) {
    console.error('Error fetching exercise library:', error)
    throw new Error(error.message)
  }

  return data
}

/**
 * Save exercise to server
 */
export async function saveExerciseToServer(exercise) {
  // Get current user ID
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('User not authenticated')

  const { data, error } = await supabase
    .from('exercise_library')
    .upsert({
      id: exercise.id,
      user_id: user.id,  // Add user_id
      name: exercise.name,
      equipment_type: exercise.equipment_type,
      muscle_group: exercise.muscleGroup,
      description: exercise.description,
      is_custom: exercise.isCustom !== undefined ? exercise.isCustom : true
    })
    .select()
    .single()

  if (error) {
    console.error('Error saving exercise:', error)
    throw new Error(error.message)
  }

  return data
}

/**
 * Batch save multiple exercises to server
 */
export async function batchSaveExercisesToServer(exercises) {
  // Get current user ID
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('User not authenticated')

  const exercisesData = exercises.map(ex => ({
    id: ex.id || crypto.randomUUID(),  // Generate UUID if not provided
    user_id: user.id,  // Add user_id
    name: ex.name,
    zh_name: ex.zhName,  // Add Chinese name
    equipment_type: ex.equipment_type,
    muscle_group: ex.muscleGroup,
    description: ex.description,
    is_custom: ex.isCustom !== undefined ? ex.isCustom : false
  }))

  const { error } = await supabase
    .from('exercise_library')
    .upsert(exercisesData)

  if (error) {
    console.error('Error batch saving exercises:', error)
    throw new Error(error.message)
  }

  return exercisesData.map(ex => ({ id: ex.id, success: true }))
}

/**
 * Delete exercise from server
 */
export async function deleteExerciseFromServer(id) {
  const { error } = await supabase
    .from('exercise_library')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting exercise:', error)
    throw new Error(error.message)
  }

  return { success: true }
}

// ============================================
// Weight Records API
// ============================================

/**
 * Fetch all weight records from server
 */
export async function fetchWeightRecords() {
  const { data, error } = await supabase
    .from('weight_records')
    .select('*')
    .order('date', { ascending: false })

  if (error) {
    console.error('Error fetching weight records:', error)
    throw new Error(error.message)
  }

  return data.map(record => ({
    ...record,
    bodyFat: record.body_fat // Map snake_case to camelCase
  }))
}

/**
 * Save weight record to server
 */
export async function saveWeightRecordToServer(record) {
  // Get current user ID
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('User not authenticated')

  const { data, error } = await supabase
    .from('weight_records')
    .upsert({
      id: record.id,
      user_id: user.id,  // Add user_id
      weight: record.weight,
      body_fat: record.bodyFat,
      date: record.date
    })
    .select()
    .single()

  if (error) {
    console.error('Error saving weight record:', error)
    throw new Error(error.message)
  }

  return data
}

/**
 * Delete weight record from server
 */
export async function deleteWeightRecordFromServer(id) {
  const { error } = await supabase
    .from('weight_records')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting weight record:', error)
    throw new Error(error.message)
  }

  return { success: true }
}

/**
 * Sync multiple weight records (legacy - for compatibility)
 */
export async function syncWeightRecords(records) {
  const results = []

  for (const record of records) {
    try {
      await saveWeightRecordToServer(record)
      results.push({ id: record.id, success: true })
    } catch (error) {
      results.push({ id: record.id, success: false, error: error.message })
    }
  }

  return results
}

/**
 * Batch sync multiple weight records (optimized)
 */
export async function batchSyncWeightRecords(records) {
  // Get current user ID
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('User not authenticated')

  const recordsData = records.map(r => ({
    id: r.id,
    user_id: user.id,  // Add user_id
    weight: r.weight,
    body_fat: r.bodyFat,
    date: r.date
  }))

  const { error } = await supabase
    .from('weight_records')
    .upsert(recordsData)

  if (error) {
    console.error('Error batch syncing weight records:', error)
    throw new Error(error.message)
  }

  return records.map(r => ({ id: r.id, success: true }))
}

// ============================================
// Delta Sync API (for future optimization)
// ============================================

/**
 * Fetch delta workouts since lastSyncTime
 */
export async function fetchDeltaWorkouts(lastSyncTime = null) {
  let query = supabase
    .from('workouts')
    .select(`
      *,
      exercises (*)
    `)
    .order('updated_at', { ascending: false })

  if (lastSyncTime) {
    query = query.gt('updated_at', lastSyncTime)
  }

  const { data, error } = await query

  if (error) {
    console.error('Error fetching delta workouts:', error)
    throw new Error(error.message)
  }

  // For simplicity, return all as "added" for now
  // In a more sophisticated implementation, track creates/updates/deletes
  return {
    added: lastSyncTime ? [] : data,
    modified: lastSyncTime ? data : [],
    deleted: [],
    syncTime: new Date().toISOString()
  }
}

/**
 * Fetch delta exercises since lastSyncTime
 */
export async function fetchDeltaExercises(lastSyncTime = null) {
  let query = supabase
    .from('exercise_library')
    .select('*')
    .order('updated_at', { ascending: false })

  if (lastSyncTime) {
    query = query.gt('updated_at', lastSyncTime)
  }

  const { data, error } = await query

  if (error) {
    console.error('Error fetching delta exercises:', error)
    throw new Error(error.message)
  }

  return {
    added: lastSyncTime ? [] : data,
    modified: lastSyncTime ? data : [],
    deleted: [],
    syncTime: new Date().toISOString()
  }
}

/**
 * Fetch delta weight records since lastSyncTime
 */
export async function fetchDeltaWeightRecords(lastSyncTime = null) {
  let query = supabase
    .from('weight_records')
    .select('*')
    .order('updated_at', { ascending: false })

  if (lastSyncTime) {
    query = query.gt('updated_at', lastSyncTime)
  }

  const { data, error } = await query

  if (error) {
    console.error('Error fetching delta weight records:', error)
    throw new Error(error.message)
  }

  return {
    added: lastSyncTime ? [] : data,
    modified: lastSyncTime ? data : [],
    deleted: [],
    syncTime: new Date().toISOString()
  }
}

// ============================================
// Authentication API (now using Supabase Auth)
// ============================================

/**
 * Login with email and password
 * Returns { token, user } on success
 */
export async function loginUser(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  })

  if (error) {
    console.error('Login error:', error)
    throw new Error(error.message)
  }

  return {
    token: data.session.access_token,
    user: {
      id: data.user.id,
      email: data.user.email,
      name: data.user.user_metadata?.name || data.user.email
    }
  }
}

/**
 * Create a new user (signup)
 */
export async function createUser(email, password, name) {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name: name || email.split('@')[0]
        }
      }
    })

    if (error) throw error

    return {
      success: true,
      data: {
        id: data.user.id,
        email: data.user.email,
        name: data.user.user_metadata?.name || email.split('@')[0]
      }
    }
  } catch (error) {
    console.error('Signup error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get all users (not applicable for Supabase - removed for security)
 * In Supabase, user management should be done via Dashboard
 */
export async function getUsers() {
  console.warn('getUsers() is not available with Supabase Auth')
  return { success: false, error: 'Not implemented with Supabase Auth' }
}

// ============================================
// Unified Data Fetch API
// ============================================

/**
 * Fetch all user data in a single API call
 * Returns all workouts, exercises, weight records, and templates with their exercises
 * This is more efficient than making separate API calls
 */
export async function fetchAllData() {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    // Fetch all data in parallel using Promise.all
    const [workouts, exerciseLibrary, weightRecords, templates] = await Promise.all([
      // Fetch workouts with exercises
      supabase
        .from('workouts')
        .select('*, exercises (*)')
        .eq('user_id', user.id)
        .order('date', { ascending: false })
        .then(({ data, error }) => {
          if (error) throw error
          return data || []
        }),

      // Fetch exercise library
      supabase
        .from('exercise_library')
        .select('*')
        .eq('user_id', user.id)
        .order('name')
        .then(({ data, error }) => {
          if (error) throw error
          return data || []
        }),

      // Fetch weight records
      supabase
        .from('weight_records')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false })
        .then(({ data, error }) => {
          if (error) throw error
          return (data || []).map(record => ({
            ...record,
            bodyFat: record.body_fat
          }))
        }),

      // Fetch workout templates with exercises
      supabase
        .from('workout_templates')
        .select(`
          *,
          template_exercises (*)
        `)
        .eq('user_id', user.id)
        .order('last_used_at', { ascending: false, nullsFirst: false })
        .then(({ data, error }) => {
          if (error) throw error
          return data || []
        })
    ])

    return {
      workouts,
      exerciseLibrary,
      weightRecords,
      templates,
      syncTime: new Date().toISOString()
    }
  } catch (error) {
    console.error('Error fetching all data:', error)
    throw error
  }
}

/**
 * Fetch delta data since lastSyncTime in a single API call
 * Returns all changed data (workouts, exercises, weight records, templates)
 */
export async function fetchAllDeltaData(lastSyncTime = null) {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    const syncTime = new Date().toISOString()

    // Fetch all delta data in parallel
    const [workouts, exerciseLibrary, weightRecords, templates] = await Promise.all([
      // Fetch delta workouts
      (async () => {
        let query = supabase
          .from('workouts')
          .select('*, exercises (*)')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false })

        if (lastSyncTime) {
          query = query.gt('updated_at', lastSyncTime)
        }

        const { data, error } = await query
        if (error) throw error

        return {
          added: lastSyncTime ? [] : (data || []),
          modified: lastSyncTime ? (data || []) : [],
          deleted: []
        }
      })(),

      // Fetch delta exercises
      (async () => {
        let query = supabase
          .from('exercise_library')
          .select('*')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false })

        if (lastSyncTime) {
          query = query.gt('updated_at', lastSyncTime)
        }

        const { data, error } = await query
        if (error) throw error

        return {
          added: lastSyncTime ? [] : (data || []),
          modified: lastSyncTime ? (data || []) : [],
          deleted: []
        }
      })(),

      // Fetch delta weight records
      (async () => {
        let query = supabase
          .from('weight_records')
          .select('*')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false })

        if (lastSyncTime) {
          query = query.gt('updated_at', lastSyncTime)
        }

        const { data, error } = await query
        if (error) throw error

        return {
          added: lastSyncTime ? [] : (data || []).map(r => ({ ...r, bodyFat: r.body_fat })),
          modified: lastSyncTime ? (data || []).map(r => ({ ...r, bodyFat: r.body_fat })) : [],
          deleted: []
        }
      })(),

      // Fetch delta templates
      (async () => {
        let query = supabase
          .from('workout_templates')
          .select('*, template_exercises (*)')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false })

        if (lastSyncTime) {
          query = query.gt('updated_at', lastSyncTime)
        }

        const { data, error } = await query
        if (error) throw error

        return {
          added: lastSyncTime ? [] : (data || []),
          modified: lastSyncTime ? (data || []) : [],
          deleted: []
        }
      })()
    ])

    return {
      workouts,
      exerciseLibrary,
      weightRecords,
      templates,
      syncTime
    }
  } catch (error) {
    console.error('Error fetching delta data:', error)
    throw error
  }
}

// ============================================
// Exercise Reset API
// ============================================

/**
 * Reset default exercises
 * Deletes all non-custom exercises and reimports default exercises
 */
export async function resetDefaultExercises(defaultExercises) {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    // Delete all non-custom exercises (is_custom = false)
    const { error: deleteError } = await supabase
      .from('exercise_library')
      .delete()
      .eq('user_id', user.id)
      .eq('is_custom', false)

    if (deleteError) throw deleteError

    // Reimport default exercises with new IDs
    const exercisesData = defaultExercises.map(ex => ({
      id: crypto.randomUUID(),  // Generate new UUID
      user_id: user.id,
      name: ex.name,
      zh_name: ex.zhName,  // Add Chinese name
      equipment_type: ex.equipment_type,
      muscle_group: ex.muscleGroup,
      description: ex.description,
      is_custom: false
    }))

    const { error: insertError } = await supabase
      .from('exercise_library')
      .insert(exercisesData)

    if (insertError) throw insertError

    return {
      success: true,
      count: exercisesData.length
    }
  } catch (error) {
    console.error('Error resetting default exercises:', error)
    throw error
  }
}
